import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Select from '../../../components/ui/Select';
import { Award } from 'lucide-react';
import { PageLayout } from '../../../components/ui/PageLayout';
import SkillsInventorySection from './components/SkillsInventorySection';
import StrengthClustersSection from './components/StrengthClustersSection';
import GapIndicatorsSection from './components/GapIndicatorsSection';
import { getCompanySkillsInventory, getCompanyJobTitles } from '../../../api/Employee/skills';
import { getDepartments } from '../../../api/General/Company';
import { SkillInventoryView, SkillFilterInput, ProficiencyLevel } from '../../../models/employee';
import { Department } from '../../../models/general';

type ViewMode = 'overview' | 'skills' | 'strengths' | 'gaps';

interface SkillInventoryItem {
  skill: string;
  totalEmployees: number;
  byProficiency: {
    Strong: number;
    Working: number;
    Awareness: number;
  };
  departments: string[];
  trend: 'increasing' | 'decreasing' | 'stable';
}

interface StrengthCluster {
  strength: string;
  frequency: number;
  departments: string[];
  confidence: 'High' | 'Medium' | 'Low';
  emergingTrend: boolean;
}

interface GapIndicator {
  skill: string;
  requiredBy: string[];
  requiredCount: number;
  availableCount: number;
  gap: number;
  priority: 'high' | 'medium' | 'low';
  recommendation: string;
}

const HRSkillsStrengthsOverview: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const companyId = useSelector((state: any) => state.user.company?.id);
  const [selectedDepartment, setSelectedDepartment] = useState('all');
  const [selectedRole, setSelectedRole] = useState('all');
  const [viewMode, setViewMode] = useState<ViewMode>('overview');
  const [loading, setLoading] = useState(false);
  const [departments, setDepartments] = useState<Department[]>([]);
  const [jobTitles, setJobTitles] = useState<string[]>([]);

  // Data from API
  const [skillsInventory, setSkillsInventory] = useState<SkillInventoryItem[]>([]);

  // Strengths data (to be implemented with actual API later)
  const [strengthClusters] = useState<StrengthCluster[]>([]);

  // Gaps data (to be implemented with actual API later)
  const [gapIndicators] = useState<GapIndicator[]>([]);

  // Load departments and job titles on mount
  useEffect(() => {
    const loadFilters = async () => {
      if (!companyId) return;
      try {
        // Load departments
        const deptRes = await getDepartments(companyId);
        setDepartments(deptRes?.payload ?? []);

        // Load job titles
        const titlesRes = await getCompanyJobTitles();
        if (titlesRes.status === 200 && titlesRes.payload) {
          setJobTitles(titlesRes.payload);
        }
      } catch (e) {
        console.error('Failed to load filters', e);
      }
    };
    loadFilters();
  }, [companyId]);

  // Build department options from API data
  const departmentOptions = [
    { value: 'all', label: t('All Departments') },
    ...departments.map(d => ({ value: d.id.toString(), label: d.name }))
  ];

  const roleOptions = [
    { value: 'all', label: t('All Roles') },
    ...jobTitles.map(title => ({ value: title, label: title }))
  ];

  useEffect(() => {
    loadSkillsInventory();
  }, [selectedDepartment]);

  const loadSkillsInventory = async () => {
    setLoading(true);
    try {
      const filter: SkillFilterInput = {};
      if (selectedDepartment !== 'all') {
        filter.departmentId = parseInt(selectedDepartment, 10);
      }

      const result = await getCompanySkillsInventory(filter);
      if (result.status === 200 && result.payload) {
        // Transform API data to match the UI component format
        const transformed: SkillInventoryItem[] = result.payload.map((item: SkillInventoryView) => ({
          skill: item.skillName,
          totalEmployees: item.totalEmployees,
          byProficiency: item.byProficiency,
          departments: item.departments,
          trend: 'stable' as const // TODO: Implement trend tracking
        }));
        setSkillsInventory(transformed);
      }
    } catch (error) {
      console.error('Failed to load skills inventory:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleExport = () => {
    console.log('Exporting organizational capability report...');
  };

  const handleRefresh = () => {
    loadSkillsInventory();
  };

  return (
    <PageLayout
      title={t('Skills & Strengths Overview')}
      description={t('Organizational capability analytics and workforce development insights')}
      icon={Award}
      accentColor="amber"
      maxWidth="1600px"
      headerActions={
        <div className="flex items-center space-x-3">
          <Button
            variant="outline"
            iconName="RefreshCw"
            onClick={handleRefresh}
            loading={loading}
          >
            {t('Refresh')}
          </Button>
          <Button
            variant="outline"
            iconName="Download"
            onClick={handleExport}
          >
            {t('Export Report')}
          </Button>
        </div>
      }
    >
        {/* View Mode Tabs */}
        <div className="mb-8">
          <div className="flex items-center space-x-2 border-b border-border">
            <button
              onClick={() => setViewMode('overview')}
              className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
                viewMode === 'overview' ? 'border-primary text-primary' : 'border-transparent text-gray-600 hover:text-gray-900'
              }`}
            >
              <div className="flex items-center space-x-2">
                <Icon name="LayoutDashboard" size={16} />
                <span>{t('Overview')}</span>
              </div>
            </button>
            <button
              onClick={() => setViewMode('skills')}
              className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
                viewMode === 'skills' ? 'border-primary text-primary' : 'border-transparent text-gray-600 hover:text-gray-900'
              }`}
            >
              <div className="flex items-center space-x-2">
                <Icon name="Award" size={16} />
                <span>{t('Skills Inventory')}</span>
              </div>
            </button>
            <button
              onClick={() => setViewMode('strengths')}
              className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
                viewMode === 'strengths' ? 'border-primary text-primary' : 'border-transparent text-gray-600 hover:text-gray-900'
              }`}
            >
              <div className="flex items-center space-x-2">
                <Icon name="Sparkles" size={16} />
                <span>{t('Strength Clusters')}</span>
              </div>
            </button>
            <button
              onClick={() => setViewMode('gaps')}
              className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
                viewMode === 'gaps' ? 'border-primary text-primary' : 'border-transparent text-gray-600 hover:text-gray-900'
              }`}
            >
              <div className="flex items-center space-x-2">
                <Icon name="AlertTriangle" size={16} />
                <span>{t('Gap Analysis')}</span>
              </div>
            </button>
          </div>
        </div>

        {/* Filters */}
        <div className="mb-6 bg-card rounded-lg shadow-sm border border-border p-4">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div className="flex items-center space-x-2">
              <Icon name="Filter" size={18} className="text-gray-500" />
              <span className="text-sm font-medium text-gray-700">{t('Filters:')}</span>
            </div>
            <div className="flex flex-wrap gap-3">
              <Select
                options={departmentOptions}
                value={selectedDepartment}
                onChange={setSelectedDepartment}
                placeholder={t('Select department')}
                className="w-full sm:w-48"
              />
              <Select
                options={roleOptions}
                value={selectedRole}
                onChange={setSelectedRole}
                placeholder={t('Select role level')}
                className="w-full sm:w-48"
              />
            </div>
          </div>
        </div>

        {/* Content Based on View Mode */}
        {viewMode === 'overview' && (
          <div className="space-y-6">
            {/* Summary Cards */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="bg-card rounded-lg shadow-sm border border-border p-6">
                <div className="flex items-center justify-between mb-4">
                  <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                    <Icon name="Award" size={24} className="text-blue-600" />
                  </div>
                  <Icon name="TrendingUp" size={20} className="text-green-500" />
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-1">{skillsInventory?.length}</h3>
                <p className="text-sm text-gray-600">{t('Tracked Skills')}</p>
                <p className="text-xs text-gray-500 mt-2">{t('Across {{count}} departments', { count: departments.length || departmentOptions?.length - 1 })}</p>
              </div>

              <div className="bg-card rounded-lg shadow-sm border border-border p-6">
                <div className="flex items-center justify-between mb-4">
                  <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center">
                    <Icon name="Sparkles" size={24} className="text-purple-600" />
                  </div>
                  <Icon name="TrendingUp" size={20} className="text-green-500" />
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-1">{strengthClusters?.length}</h3>
                <p className="text-sm text-gray-600">{t('Identified Strengths')}</p>
                <p className="text-xs text-gray-500 mt-2">{t('{{count}} emerging trends', { count: strengthClusters?.filter(s => s?.emergingTrend)?.length })}</p>
              </div>

              <div className="bg-card rounded-lg shadow-sm border border-border p-6">
                <div className="flex items-center justify-between mb-4">
                  <div className="w-12 h-12 bg-orange-100 rounded-lg flex items-center justify-center">
                    <Icon name="AlertTriangle" size={24} className="text-orange-600" />
                  </div>
                  <Icon name="AlertCircle" size={20} className="text-orange-500" />
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-1">{gapIndicators?.filter(g => g?.priority === 'high')?.length}</h3>
                <p className="text-sm text-gray-600">{t('High Priority Gaps')}</p>
                <p className="text-xs text-gray-500 mt-2">{t('{{count}} total gaps identified', { count: gapIndicators?.length })}</p>
              </div>
            </div>

            {/* All Sections with View More navigation */}
            <SkillsInventorySection
              data={skillsInventory}
              onViewMore={() => setViewMode('skills')}
            />
            <StrengthClustersSection
              data={strengthClusters}
              onViewMore={() => setViewMode('strengths')}
            />
            <GapIndicatorsSection
              data={gapIndicators}
              onViewMore={() => setViewMode('gaps')}
            />
          </div>
        )}

        {viewMode === 'skills' && <SkillsInventorySection data={skillsInventory} expanded />}
        {viewMode === 'strengths' && <StrengthClustersSection data={strengthClusters} expanded />}
        {viewMode === 'gaps' && <GapIndicatorsSection data={gapIndicators} expanded />}
    </PageLayout>
  );
};

export default HRSkillsStrengthsOverview;
